import React, { useMemo } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import fr from 'date-fns/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './EmployeeList.css';

const locales = { fr };
const localizer = dateFnsLocalizer({
  format: (date, fmt, options) => format(date, fmt, { locale: fr, ...options }),
  parse: (value, fmt, refDate) => parse(value, fmt, refDate, { locale: fr }),
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const statusColors = {
  APPROVED: '#16a34a',
  PENDING: '#f59e0b',
  REJECTED: '#ef4444',
  CORRECTED: '#0ea5e9',
};

const normalizeDate = (value) => {
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const parsed = parse(value, 'dd/MM/yyyy', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const combineDateAndTime = (dateValue, timeValue) => {
  const baseDate = normalizeDate(dateValue);
  if (!baseDate) return null;
  if (!timeValue) return baseDate;
  const parsed = parse(`${format(baseDate, 'yyyy-MM-dd')} ${timeValue}`, 'yyyy-MM-dd HH:mm', new Date());
  return Number.isNaN(parsed.getTime()) ? baseDate : parsed;
};

const TimesheetCalendar = ({ timesheets = [] }) => {
  const events = useMemo(() => {
    return (timesheets || [])
      .map((ts) => {
        const dateValue = ts.date || ts.formattedDate;
        const start = combineDateAndTime(dateValue, ts.checkInTime || ts.formattedCheckInTime);
        const end = combineDateAndTime(dateValue, ts.checkOutTime || ts.formattedCheckOutTime) || start;
        const status = (ts.status || ts.statusLabel || '').toUpperCase();
        if (!start) return null;
        return {
          id: ts.id,
          title: `${ts.employeeName || 'Employé'} (${ts.formattedCheckInTime || ts.checkInTime || '--:--'} - ${
            ts.formattedCheckOutTime || ts.checkOutTime || '--:--'
          })`,
          start,
          end: end || start,
          allDay: !ts.checkInTime && !ts.checkOutTime,
          status,
          notes: ts.notes,
        };
      })
      .filter(Boolean);
  }, [timesheets]);

  if (!events.length) {
    return <div className="empty-state">Aucun pointage à afficher.</div>;
  }

  const eventPropGetter = (event) => {
    const color = statusColors[event.status] || '#2563eb';
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        color: '#fff',
      },
    };
  };

  const messages = {
    allDay: 'Journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Pointage',
    showMore: (total) => `+ ${total} de plus`,
  };

  return (
    <div className="table-container" style={{ minHeight: 520 }}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
        defaultView={Views.MONTH}
        style={{ height: 500 }}
        messages={messages}
        eventPropGetter={eventPropGetter}
        tooltipAccessor={(event) => event.notes || event.title}
      />
    </div>
  );
};

export default TimesheetCalendar;

